import { createRouter, createWebHistory } from 'vue-router'
import { useSettingsStore } from '../stores/settingsStore'
import { useAuthStore } from '../stores/authStore'
import { resolveRouteSEO, applyDOMMetaTags } from '../utils/seo'
import {
    saveScrollPosition,
    getSavedScrollPosition,
    restoreScrollPosition,
    stopScrollRestoration,
    isPopstateNavigation,
    resetPopNavigationFlag,
    setActiveRouteForScroll
} from '../utils/scrollRestoration'
import Home from '../pages/Home.vue'

const routes = [
    {
        path: '/',
        component: Home,
        meta: { title: 'Outdoor Lander - บ้านเก็บของและโรงเรือนสำเร็จรูประดับพรีเมียม', description: 'Outdoor Lander จำหน่ายและติดตั้งบ้านเก็บของ โรงเรือน และโกดังสำเร็จรูปคุณภาพสูง ได้มาตรฐานสากล' }
    },
    {
        path: '/products',
        component: () => import('../pages/Products.vue'),
        meta: { title: 'แคตตาล็อกสินค้า - Outdoor Lander บ้านเก็บของพรีเมียม', description: 'เลือกชมแคตตาล็อกสินค้ามากกว่า 100 รายการ ทั้งบ้านเก็บของ PP/HDPE, โรงจอดรถ และโรงเรือนเพาะปลูก' }
    },
    {
        path: '/products/category/:category',
        component: () => import('../pages/Products.vue'),
        meta: { title: 'สินค้าหมวดหมู่ - Outdoor Lander', description: 'สินค้าในหมวดหมู่ที่เลือก - Outdoor Lander บ้านเก็บของและโรงเรือนพรีเมียม' }
    },
    {
        path: '/products/:id',
        component: () => import('../pages/ProductDetail.vue'),
        meta: { title: 'รายละเอียดสินค้า - Outdoor Lander', description: 'ข้อมูลรายละเอียด สเปค และราคาของสินค้า โครงสร้างเหล็กแข็งแรงทนทาน' }
    },
    {
        path: '/services',
        component: () => import('../pages/Services.vue'),
        meta: { title: 'บริการของเรา - Outdoor Lander ผู้เชี่ยวชาญด้านงานติดตั้ง', description: 'บริการให้คำปรึกษา ประเมินหน้างาน และติดตั้งบ้านเก็บของอย่างมืออาชีพโดยวิศวกรและช่างผู้ชำนาญการ' }
    },
    {
        path: '/projects',
        component: () => import('../pages/Projects.vue'),
        meta: { title: 'แฟ้มผลงาน - Outdoor Lander', description: 'ชมผลงานการติดตั้งจริงของ Outdoor Lander ทั่วประเทศ สร้างความมั่นใจด้วยมาตรฐานระดับสากล' }
    },
    {
        path: '/projects/:slug',
        component: () => import('../pages/ProjectDetail.vue'),
        meta: { title: 'รายละเอียดผลงาน - Outdoor Lander' }
    },
    {
        path: '/installation-guide',
        component: () => import('../pages/InstallationGuide.vue'),
        meta: { title: 'คู่มือการเตรียมพื้นที่และติดตั้ง - Outdoor Lander', description: 'คลิปวิดีโอคู่มือและข้อมูลการเตรียมพื้นที่หน้างานก่อนการติดตั้งบ้านเก็บของหรือโรงเรือน' }
    },
    {
        path: '/payment-methods',
        alias: ['/payment-guide'],
        component: () => import('../pages/PaymentGuide.vue'),
        meta: { title: 'วิธีการสั่งซื้อและชำระเงิน - Outdoor Lander', description: 'ขั้นตอนการสั่งซื้อสินค้า ช่องทางการชำระเงิน และนโยบายทางการเงิน' }
    },
    {
        path: '/about',
        component: () => import('../pages/About.vue'),
        meta: { title: 'เกี่ยวกับเรา - Outdoor Lander', description: 'ทำความรู้จักกับเรา ผู้เชี่ยวชาญด้านสินค้านำเข้าอุตสาหกรรมด้วยประสบการณ์กว่า 20 ปี' }
    },
    {
        path: '/ai-consultant',
        component: () => import('../pages/AIConsultant.vue'),
        meta: { title: 'AI ผู้ช่วยส่วนตัว - วิเคราะห์งบประมาณและพื้นที่', description: 'ทดลองใช้ AI Consultant ของ Outdoor Lander เพื่อช่วยคำนวณพื้นที่และประเมินงบประมาณของคุณ ฟรี' }
    },
    {
        path: '/verify-email',
        component: () => import('../pages/VerifyEmail.vue'),
        meta: { title: 'ยืนยันอีเมลของคุณ - Outdoor Lander', description: 'ยืนยันตัวตนเพื่อเข้าถึงสิทธิประโยชน์จาก Outdoor Lander แบบเต็มรูปแบบ' }
    },
    {
        path: '/auth/line/callback',
        component: () => import('../pages/LineCallback.vue'),
        meta: { title: 'กำลังเข้าสู่ระบบ - Outdoor Lander' }
    },
    {
        path: '/forgot-password',
        component: () => import('../pages/ForgotPassword.vue'),
        meta: { title: 'ลืมรหัสผ่าน - Outdoor Lander', description: 'ขอลิงก์รีเซ็ตรหัสผ่านผ่านอีเมล' }
    },
    {
        path: '/reset-password',
        component: () => import('../pages/ResetPassword.vue'),
        meta: { title: 'ตั้งรหัสผ่านใหม่ - Outdoor Lander', description: 'ตั้งรหัสผ่านใหม่สำหรับบัญชีของคุณ' }
    },
    {
        path: '/quotation',
        component: () => import('../pages/Quotation.vue'),
        meta: { title: 'ขอใบเสนอราคา - Outdoor Lander', description: 'กรอกแบบฟอร์มเพื่อขอรับใบเสนอราคาบ้านเก็บของ หรือนัดหมายช่างประเมินหน้างาน' }
    },
    {
        path: '/promotions',
        component: () => import('../pages/Promotions.vue'),
        meta: { title: 'ศูนย์รวมโปรโมชั่นและข้อเสนอพิเศษ - Outdoor Lander', description: 'ศูนย์รวมโปรโมชั่น ส่วนลดพิเศษ และข้อเสนอสุดคุ้มจาก Outdoor Lander' }
    },
    {
        path: '/contact',
        component: () => import('../pages/Contact.vue'),
        meta: { title: 'ติดต่อเรา - Outdoor Lander', description: 'ช่องทางการติดต่อ Outdoor Lander ที่อยู่สำนักงาน อีเมล เบอร์โทรศัพท์ และ Line Official' }
    },
    {
        path: '/login',
        redirect: '/'
    },
    {
        path: '/profile',
        component: () => import('../pages/Profile.vue'),
        meta: { title: 'บัญชีของฉัน - Outdoor Lander', requiresCustomerAuth: true }
    },
    {
        path: '/cart',
        component: () => import('../pages/Cart.vue'),
        meta: { title: 'ตะกร้าสินค้า - Outdoor Lander', description: 'ตรวจสอบรายการสินค้าและดำเนินการสั่งซื้อ Outdoor Lander' }
    },
    {
        path: '/checkout',
        component: () => import('../pages/Checkout.vue'),
        meta: { title: 'ชำระเงิน - Outdoor Lander', description: 'ดำเนินการชำระเงินและกรอกข้อมูลจัดส่ง' }
    },
    {
        path: '/order-success/:id',
        component: () => import('../pages/OrderSuccess.vue'),
        meta: { title: 'สั่งซื้อสำเร็จ - Outdoor Lander' }
    },
    {
        path: '/track-order',
        component: () => import('../pages/TrackOrder.vue'),
        meta: { title: 'ตรวจสอบคำสั่งซื้อ - Outdoor Lander', description: 'ตรวจสอบสถานะและรายละเอียดคำสั่งซื้อของคุณ' }
    },
    {
        path: '/recently-viewed',
        component: () => import('../pages/RecentlyViewedPage.vue'),
        meta: { title: 'ประวัติการดูสินค้า - Outdoor Lander', description: 'ประวัติสินค้าที่คุณเคยดู' }
    },
    {
        path: '/compare',
        component: () => import('../pages/Compare.vue'),
        meta: { title: 'เปรียบเทียบสินค้า - Outdoor Lander', description: 'เปรียบเทียบคุณสมบัติและราคาของสินค้าแบบ side-by-side' }
    },
    {
        path: '/space-calculator',
        component: () => import('../pages/SpaceCalculator.vue'),
        meta: { title: 'ประเมินพื้นที่และราคา - Outdoor Lander', description: 'เครื่องมือคำนวณขนาดพื้นที่ แนะนำสินค้าที่เหมาะสมพร้อมประเมินราคา' }
    },

    // Legal & Utilities
    {
        path: '/privacy-policy',
        alias: ['/privacy'],
        component: () => import('../pages/PrivacyPolicy.vue'),
        meta: { title: 'นโยบายความเป็นส่วนตัว - Outdoor Lander', description: 'รายละเอียดนโยบายความเป็นส่วนตัวและการคุ้มครองข้อมูลส่วนบุคคล (PDPA)' }
    },
    {
        path: '/terms-of-service',
        alias: ['/terms', '/terms-of-use'],
        component: () => import('../pages/TermsOfService.vue'),
        meta: { title: 'เงื่อนไขการให้บริการ - Outdoor Lander', description: 'ข้อกำหนดและเงื่อนไขการใช้งานแพลตฟอร์ม Outdoor Lander รวมถึงนโยบายการรับประกันสินค้า' }
    },
    {
        path: '/cookie-policy',
        alias: ['/cookies'],
        component: () => import('../pages/CookiePolicy.vue'),
        meta: { title: 'นโยบายการใช้คุกกี้ - Outdoor Lander', description: 'รายละเอียดนโยบายการใช้คุกกี้ (Cookie Policy) ของเว็บไซต์ Outdoor Lander' }
    },
    {
        path: '/warranty-policy',
        alias: ['/warranty'],
        component: () => import('../pages/WarrantyPolicy.vue'),
        meta: { title: 'นโยบายการรับประกัน - Outdoor Lander', description: 'เงื่อนไขการรับประกันและข้อยกเว้นสำหรับสินค้าและบริการก่อสร้างโกดังของ Outdoor Lander' }
    },
    {
        path: '/blog',
        component: () => import('../pages/Blog.vue'),
        meta: { title: 'บทความและความรู้ - Outdoor Lander', description: 'บทความให้ความรู้เกี่ยวกับบ้านเก็บของ โรงเรือน การดูแลรักษา และเคล็ดลับจัดพื้นที่' }
    },
    {
        path: '/blog/:slug',
        component: () => import('../pages/BlogDetail.vue'),
        meta: { title: 'บทความ - Outdoor Lander' }
    },
    {
        path: '/articles',
        redirect: '/blog'
    },
    {
        path: '/articles/:slug',
        redirect: to => `/blog/${to.params.slug}`
    },
    {
        path: '/maintenance',
        component: () => import('../pages/Maintenance.vue'),
        meta: { title: 'ปิดปรับปรุงระบบชั่วคราว - Outdoor Lander', isMaintenancePage: true }
    },
    {
        path: '/error',
        component: () => import('../pages/ServerError.vue'),
        meta: { title: 'เกิดข้อผิดพลาด - Outdoor Lander' }
    },
    {
        path: '/product/:id',
        redirect: to => `/products/${to.params.id}`
    },
    {
        path: '/category/:category',
        redirect: to => `/products/category/${to.params.category}`
    },
    {
        path: '/categories/:category',
        redirect: to => `/products/category/${to.params.category}`
    },
    {
        path: '/portfolio',
        redirect: '/projects'
    },
    {
        path: '/portfolio/details/:pathMatch(.*)*',
        redirect: '/projects'
    },
    {
        path: '/portfolio/:pathMatch(.*)*',
        redirect: '/projects'
    },
    {
        path: '/:pathMatch(.*)*',
        component: () => import('../pages/NotFound.vue'),
        meta: { title: '404 ไม่พบหน้าเว็บ - Outdoor Lander' }
    },
    {
        path: '/admin/login',
        component: () => import('../pages/admin/Login.vue'),
        meta: { title: 'เข้าสู่ระบบ - Outdoor Lander Admin' }
    },
    {
        path: '/admin/orders/:id/print/:docType',
        component: () => import('../pages/admin/PrintDocument.vue'),
        meta: { title: 'พิมพ์เอกสาร - Admin' }
    },
    {
        path: '/admin',
        component: () => import('../pages/admin/AdminLayout.vue'),
        meta: { title: 'ระบบจัดการส่วนหลังบ้าน - Outdoor Lander' },
        children: [
            { path: '', component: () => import('../pages/admin/Dashboard.vue'), meta: { title: 'Dashboard - Admin' } },
            { path: 'analytics', component: () => import('../pages/admin/AnalyticsAdmin.vue'), meta: { title: 'Analytics - Admin' } },
            { path: 'homepage', component: () => import('../pages/admin/HomeAdmin.vue'), meta: { title: 'จัดการหน้าหลัก - Admin' } },
            { path: 'products', component: () => import('../pages/admin/ProductsAdmin.vue'), meta: { title: 'จัดการสินค้า - Admin' } },
            { path: 'products/new', component: () => import('../pages/admin/ProductFormAdmin.vue'), meta: { title: 'เพิ่มสินค้า - Admin' } },
            { path: 'products/:id/edit', component: () => import('../pages/admin/ProductFormAdmin.vue'), meta: { title: 'แก้ไขสินค้า - Admin' } },
            { path: 'categories', component: () => import('../pages/admin/CategoriesAdmin.vue'), meta: { title: 'จัดการหมวดหมู่ - Admin' } },
            { path: 'categories/new', component: () => import('../pages/admin/CategoryFormAdmin.vue'), meta: { title: 'เพิ่มหมวดหมู่ - Admin' } },
            { path: 'categories/:id/edit', component: () => import('../pages/admin/CategoryFormAdmin.vue'), meta: { title: 'แก้ไขหมวดหมู่ - Admin' } },
            { path: 'leads', component: () => import('../pages/admin/LeadsAdmin.vue'), meta: { title: 'ระบบหลังบ้าน Leads - Admin' } },
            { path: 'quotations', component: () => import('../pages/admin/LeadsAdmin.vue'), meta: { title: 'ประวัติและจัดการใบเสนอราคา - Admin' } },
            { path: 'leads/:id', component: () => import('../pages/admin/LeadDetailAdmin.vue'), meta: { title: 'รายละเอียดใบเสนอราคา - Admin' } },
            { path: 'quotations/:id', component: () => import('../pages/admin/LeadDetailAdmin.vue'), meta: { title: 'รายละเอียดใบเสนอราคา - Admin' } },
            { path: 'calculator', component: () => import('../pages/admin/InstallationCalculatorAdmin.vue'), meta: { title: 'เครื่องมือคำนวณค่าติดตั้ง - Admin' } },
            { path: 'orders', component: () => import('../pages/admin/OrdersAdmin.vue'), meta: { title: 'จัดการคำสั่งซื้อ - Admin' } },
            { path: 'orders/:id', component: () => import('../pages/admin/OrderDetailAdmin.vue'), meta: { title: 'รายละเอียดคำสั่งซื้อ - Admin' } },
            { path: 'inbox', component: () => import('../pages/admin/InboxAdmin.vue'), meta: { title: 'กล่องข้อความ - Admin' } },
            { path: 'newsletter', component: () => import('../pages/admin/NewsletterAdmin.vue'), meta: { title: 'สมาชิกข่าวสาร - Admin' } },
            { path: 'contact', component: () => import('../pages/admin/ContactAdmin.vue'), meta: { title: 'จัดการข้อมูลติดต่อ - Admin' } },
            { path: 'about', component: () => import('../pages/admin/AboutAdmin.vue'), meta: { title: 'จัดการเนื้อหา เกี่ยวกับเรา - Admin' } },
            { path: 'services', component: () => import('../pages/admin/ServicesAdmin.vue'), meta: { title: 'จัดการหน้าบริการ - Admin' } },
            { path: 'projects', component: () => import('../pages/admin/ProjectsAdmin.vue'), meta: { title: 'จัดการหน้าผลงาน - Admin' } },
            { path: 'articles', component: () => import('../pages/admin/ArticlesAdmin.vue'), meta: { title: 'จัดการบทความ - Admin' } },
            { path: 'articles/new', component: () => import('../pages/admin/ArticleFormAdmin.vue'), meta: { title: 'เขียนบทความใหม่ - Admin' } },
            { path: 'articles/:id/edit', component: () => import('../pages/admin/ArticleFormAdmin.vue'), meta: { title: 'แก้ไขบทความ - Admin' } },
            { path: 'customers', component: () => import('../pages/admin/CustomersAdmin.vue'), meta: { title: 'จัดการลูกค้าสมาชิก - Admin' } },
            { path: 'customers/:id', component: () => import('../pages/admin/CustomerDetailAdmin.vue'), meta: { title: 'รายละเอียดลูกค้า - Admin' } },
            { path: 'users', component: () => import('../pages/admin/AdminUsersAdmin.vue'), meta: { title: 'จัดการผู้ดูแลระบบ - Admin' } },
            { path: 'profile', component: () => import('../pages/admin/ProfileAdmin.vue'), meta: { title: 'โปรไฟล์ผู้ดูแลระบบ - Admin' } },
            { path: 'settings', component: () => import('../pages/admin/SettingsAdmin.vue'), meta: { title: 'ตั้งค่าระบบ - Admin' } },
            { path: 'images', component: () => import('../pages/admin/ImagesAdmin.vue'), meta: { title: 'จัดการและบีบอัดรูปภาพ - Admin' } },
            { path: 'logs', component: () => import('../pages/admin/LogsAdmin.vue'), meta: { title: 'บันทึกระบบ (System Logs) - Admin' } },
            { path: 'logs/client', component: () => import('../pages/admin/LogsAdmin.vue'), meta: { title: 'ประวัติข้อผิดพลาดไคลเอ็นต์ - Admin' } },
            { path: 'logs/orders', component: () => import('../pages/admin/LogsAdmin.vue'), meta: { title: 'ประวัติกิจกรรมคำสั่งซื้อ - Admin' } },
            { path: 'logs/emails', component: () => import('../pages/admin/LogsAdmin.vue'), meta: { title: 'ประวัติส่งอีเมล (Email Logs) - Admin' } },
            { path: 'updates', component: () => import('../pages/admin/SystemUpdatesAdmin.vue'), meta: { title: 'ประวัติการอัปเดตระบบ - Admin' } },
            { path: 'policies', component: () => import('../pages/admin/PoliciesAdmin.vue'), meta: { title: 'จัดการนโยบายและข้อตกลง - Admin' } },
            { path: 'guides', component: () => import('../pages/admin/GuidesAdmin.vue'), meta: { title: 'จัดการคู่มือและวิธีการต่างๆ - Admin' } },
            { path: 'manual', component: () => import('../pages/admin/Manual.vue'), meta: { title: 'คู่มือการใช้งานแบบละเอียด - Admin' } },
            { path: 'reviews', component: () => import('../pages/admin/ReviewsAdmin.vue'), meta: { title: 'จัดการรีวิวสินค้า - Admin' } },
            { path: 'coupons', component: () => import('../pages/admin/CouponsAdmin.vue'), meta: { title: 'จัดการโค้ดส่วนลด - Admin' } },
            { path: 'promotions', component: () => import('../pages/admin/PromotionsAdmin.vue'), meta: { title: 'จัดการโปรโมชั่นร้านค้า - Admin' } },
            { path: 'promotions/new', component: () => import('../pages/admin/PromotionFormAdmin.vue'), meta: { title: 'สร้างโปรโมชั่นใหม่ - Admin' } },
            { path: 'promotions/:id/edit', component: () => import('../pages/admin/PromotionFormAdmin.vue'), meta: { title: 'แก้ไขโปรโมชั่น - Admin' } },
            { path: 'line', component: () => import('../pages/admin/LineAdmin.vue'), meta: { title: 'จัดการ Line Official - Admin' } },
            { path: 'tracking-tags', component: () => import('../pages/admin/TrackingTagsAdmin.vue'), meta: { title: 'จัดการระบบแท็ก (Tag Manager) - Admin' } },
            { path: 'bot-monitor', component: () => import('../pages/admin/BotMonitorAdmin.vue'), meta: { title: 'มอนิเตอร์บอท & AI Crawlers - Admin' } }
        ]
    }
]

if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual'
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (to.hash) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    const el = document.querySelector(to.hash)
                    if (el) {
                        resolve({ el: to.hash, behavior: 'smooth' })
                    } else {
                        resolve({ top: 0, left: 0 })
                    }
                }, 100)
            })
        }

        if (savedPosition || isPopstateNavigation()) {
            // Delegate popstate scroll restoration to restoreScrollPosition in afterEach
            // which accounts for async data expansion and preventing bottom-clamping
            return false
        }

        return { top: 0, left: 0, behavior: 'instant' }
    }
})

router.afterEach((to, from) => {
    setActiveRouteForScroll(to)

    // Admin layout has its own internal scrolling container
    if (to.path.startsWith('/admin')) {
        resetPopNavigationFlag()
        return
    }

    const isPop = isPopstateNavigation()
    const saved = isPop ? getSavedScrollPosition(to) : null

    if (saved && (saved.y > 0 || saved.x > 0)) {
        restoreScrollPosition(saved, to)
    } else if (!to.hash) {
        stopScrollRestoration()
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }

    resetPopNavigationFlag()
})

// Navigation Guard for SEO Meta Tags & State Persistence
router.beforeEach((to, from, next) => {
    // Snapshot previous page's scroll position before leaving on forward/push navigation
    if (!isPopstateNavigation()) {
        if (!from.path.startsWith('/admin')) {
            saveScrollPosition(from)
        }
        stopScrollRestoration()
    }

    const settingsStore = useSettingsStore()
    const seoData = resolveRouteSEO(to, settingsStore)
    applyDOMMetaTags(seoData, to.fullPath)

    // Helper to decode JWT token in frontend
    const parseJwt = (token) => {
        try {
            const base64Url = token.split('.')[1]
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            }).join(''))
            return JSON.parse(jsonPayload)
        } catch (e) {
            return null
        }
    }

    // Admin Authentication Guard
    if (to.path.startsWith('/admin') && to.path !== '/admin/login') {
        const token = localStorage.getItem('adminToken')
        if (!token) {
            return next('/admin/login')
        }

        // Decode and verify expiration (exp)
        const payload = parseJwt(token)
        if (!payload || !payload.exp || Date.now() >= payload.exp * 1000) {
            localStorage.removeItem('adminToken')
            localStorage.removeItem('adminUser')
            return next('/admin/login')
        }

        // Enforce role-based access control for owner-only sub-routes
        const ownerOnlyRoutes = ['/admin/users', '/admin/settings', '/admin/logs', '/admin/line', '/admin/tracking-tags']
        const isOwnerOnly = ownerOnlyRoutes.some(route => to.path === route || to.path.startsWith(route + '/'))
        if (isOwnerOnly && payload.role !== 'owner') {
            console.warn('Access denied: Owner role required for', to.path)
            return next('/admin') // Redirect to dashboard
        }
    }

    // Maintenance Mode Guard (exclude admin routes and the maintenance page itself)
    try {
        const settingsStore = useSettingsStore()
        if (settingsStore.maintenanceModeEnabled && !to.path.startsWith('/admin') && !to.meta.isMaintenancePage) {
            return next('/maintenance')
        }
        // If maintenance is OFF but user is on maintenance page, redirect to home
        if (!settingsStore.maintenanceModeEnabled && to.meta.isMaintenancePage) {
            return next('/')
        }
    } catch (e) {}

    // Online Shopping Guard
    try {
        const settingsStore = useSettingsStore()
        if (!settingsStore.isOnlineShoppingEnabled && (to.path === '/cart' || to.path === '/checkout' || to.path.startsWith('/checkout') || to.path === '/profile' || to.path === '/verify-email')) {
            return next('/')
        }
    } catch (e) {
        // Pinia might not be fully initialized on the very first route resolution
    }

    // AI Consultant Guard
    try {
        const settingsStore = useSettingsStore()
        if (!settingsStore.isAiConsultantEnabled && to.path === '/ai-consultant') {
            return next('/')
        }
    } catch (e) {
        // Pinia might not be fully initialized on the very first route resolution
    }

    // Customer Authentication Guard (for /profile and protected customer routes)
    if (to.meta.requiresCustomerAuth || to.path === '/profile' || to.path.startsWith('/profile/')) {
        const customerToken = localStorage.getItem('customer_token')
        const payload = customerToken ? parseJwt(customerToken) : null
        const isExpired = !payload || (payload.exp && Date.now() >= payload.exp * 1000)

        if (!customerToken || isExpired) {
            localStorage.removeItem('customer_token')
            localStorage.removeItem('customer_user')
            try {
                const authStore = useAuthStore()
                if (authStore.isAuthenticated) {
                    authStore.logout()
                }
            } catch (e) {}
            const fallbackPath = (from && from.path && from.path !== '/profile' && !from.path.startsWith('/profile/')) ? from.path : '/'
            return next({ path: fallbackPath, query: { ...from?.query, login: 'true' } })
        }
    }

    next()
})

export default router
